import { useEffect, useState } from 'react';
import { Button, Divider, Form, Upload } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getAccountBalanceApi, getSenderAccountsApi } from '@http/accounts';
import { swiftTjsTransactionApi } from '@http/payments';

import SenderAccountsModal from '@components/Payments/modals/SenderAccountsModal';
import ConfirmPaymentModal from '@components/Payments/modals/ConfirmPaymentModal';
import Currencies from '@components/Currency/OrienbankRates';
import Templates from '@components/Templates/Templates';

import { deadlineAC, otpConfirmAC, repeatSwiftTjsTransactionAC, verifyTransIdAC } from '@store/reducers/transactions';
import {
  isConfirmPaymentModalOpenAC,
  isSaveTemplateModalOpenAC,
  isSearchSwiftModalOpenAC,
  newSwiftTjsTemplateAC,
  swiftTjsQueryAC,
} from '@store/reducers/payments';
import { senderAccountBalanceAC, selectedSenderAccountAC, senderAccountsModalOpenAC } from '@store/reducers/accounts';
import { UploadOutlined } from '@ant-design/icons';
import SwiftTjsForm from '@components/Payments/forms/SwiftTjsForm';
import SearchSwiftModal from '@components/Payments/modals/SearchSwiftModal';
import { getCountriesApi } from '@http/global';
import { getTemplatesApi } from '@http/user';
import { pickedSwiftTjsTemplateAC } from '@store/reducers/user';
import SaveTemplateModal from '@components/Payments/modals/SaveTemplateModal';

function SwiftTjs() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const senderAccounts = useAppSelector(state => state.accountsReducer.senderAccounts);
  const swiftTjsQuery = useAppSelector(state => state.paymentsReducer.swiftTjsQuery);
  const senderAccountBalance = useAppSelector(state => state.accountsReducer.senderAccountBalance);
  const newSwiftTjsTemplate = useAppSelector(state => state.paymentsReducer.newSwiftTjsTemplate);
  const pickedSwiftTjsTemplate = useAppSelector(state => state.userReducer.pickedSwiftTjsTemplate);
  const repeatSwiftTjsTransaction = useAppSelector(state => state.transactionsReducer.repeatSwiftTjsTransaction);

  const [senderAccountCurrency, setSenderAccountCurrency] = useState('');
  const [file, setFile] = useState<any>();

  useEffect(() => {
    initializeData();

    return clearFields;
  }, []);

  useEffect(() => {
    pickedSwiftTjsTemplateHandler();
  }, [pickedSwiftTjsTemplate]);

  useEffect(() => {
    repeatSwiftTjsTransactionHandler();
  }, [repeatSwiftTjsTransaction]);

  useEffect(() => {
    dispatch(otpConfirmAC(false));
  }, [swiftTjsQuery]);

  const initializeData = () => {
    dispatch(getCountriesApi());
    dispatch(selectedSenderAccountAC({}));
    dispatch(senderAccountBalanceAC(0));
    dispatch(getTemplatesApi({ transaction_type: 'SWIFT_TJS' }));
    dispatch(
      getSenderAccountsApi({
        transaction_type: 'SWIFT_TJS',
      })
    );
  };

  const pickedSwiftTjsTemplateHandler = () => {
    setSenderAccountCurrency(pickedSwiftTjsTemplate.sending_ccy);

    fillQuery(pickedSwiftTjsTemplate, 'template');
    fillForms(pickedSwiftTjsTemplate, 'template');

    if (+pickedSwiftTjsTemplate.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: pickedSwiftTjsTemplate.sending_ac }));
    }
  };

  const repeatSwiftTjsTransactionHandler = () => {
    setSenderAccountCurrency(repeatSwiftTjsTransaction.sending_ccy);

    fillForms(repeatSwiftTjsTransaction, 'repeat');
    fillQuery(repeatSwiftTjsTransaction, 'repeat');

    if (+repeatSwiftTjsTransaction.sending_amount > 0) {
      dispatch(getAccountBalanceApi('sender', { cust_ac_no: repeatSwiftTjsTransaction.sending_ac }));
    }
  };

  const fillForms = (data: any, type: any) => {
    form.setFieldValue('sending_ac', data.sending_ac);
    form.setFieldValue('receiving_ac', data.receiving_ac);
    form.setFieldValue('receiving_ac2', data.receiving_ac);
    form.setFieldValue('receiver_name', data.receiver_name);
    form.setFieldValue('sender_address', data.sender_address);
    form.setFieldValue('sender_city', data.sender_city);
    form.setFieldValue('receiver_city', data.receiver_city);
    form.setFieldValue('receiver_country', data.receiver_country);
    form.setFieldValue('receiver_bank_swift', data.receiver_bank_swift);
    form.setFieldValue('receiver_bank_name', data.receiver_bank_name);
    form.setFieldValue('receiver_bank_country', data.receiver_bank_country);
    form.setFieldValue('receiver_address', data.receiver_address);

    dispatch(selectedSenderAccountAC(data?.sending_ac));
  };

  const fillQuery = (data: any, type: any) => {
    dispatch(
      swiftTjsQueryAC({
        sending_ac: data.sending_ac,
        receiving_ac: data.receiving_ac,
        receiving_ac2: data.receiving_ac,
        receiver_name: data.receiver_name,
        doc_no: data.doc_no,
        receiver_bank_name: data.receiver_bank_name,
        receiver_bank_swift: data.receiver_bank_swift,
        receiver_country: data.receiver_country,
        receiver_city: data.receiver_city,
        sender_city: data.sender_city,
        sender_address: data.sender_address,
        receiver_bank_country: data.receiver_bank_country,
        receiver_address: data.receiver_address,
      })
    );
  };

  const senderAccountSelect = (account: any) => {
    setSenderAccountCurrency(account.ccy);
    dispatch(swiftTjsQueryAC({ ...swiftTjsQuery, sending_ac: account.value }));

    form.setFieldValue('sending_ac', account.value);

    dispatch(senderAccountsModalOpenAC(false));
    dispatch(selectedSenderAccountAC(account));
    dispatch(getAccountBalanceApi('sender', { cust_ac_no: account.value }));
  };

  const selectSwiftHandler = (item: any) => {
    form.setFieldsValue({
      receiver_bank_swift: item.bic_code,
      receiver_bank_name: item.bank_name,
    });

    dispatch(isSearchSwiftModalOpenAC(false));
  };

  const swiftTjsTransaction = () => {
    dispatch(swiftTjsTransactionApi({ ...swiftTjsQuery, file: file?.originFileObj }, navigate, () => clearFields()));
  };

  const clearFields = () => {
    dispatch(swiftTjsQueryAC({}));
    setSenderAccountCurrency('');
    setFile(null);

    form.resetFields();

    dispatch(otpConfirmAC(false));
    dispatch(verifyTransIdAC(''));
    dispatch(senderAccountBalanceAC(0));
    dispatch(selectedSenderAccountAC({}));
    dispatch(isConfirmPaymentModalOpenAC(false));
    dispatch(repeatSwiftTjsTransactionAC({}));
    dispatch(pickedSwiftTjsTemplateAC({}));
  };

  const submit = (values: any) => {
    const { file, ...query } = values;

    if (+swiftTjsQuery.sending_amount > +senderAccountBalance) {
      form.setFields([
        {
          name: 'sending_ac',
          errors: [t('Insufficient funds in the account')],
        },
      ]);
    } else {
      dispatch(swiftTjsQueryAC(query));
      dispatch(isConfirmPaymentModalOpenAC(true));
      dispatch(deadlineAC(Date.now() + 1000 * 30));
    }
  };

  const openSaveTemplateModal = () => {
    dispatch(
      newSwiftTjsTemplateAC({
        ...newSwiftTjsTemplate,
        sending_amount: form.getFieldValue('sending_amount'),
        sending_ac: form.getFieldValue('sending_ac'),
        receiving_ac: form.getFieldValue('receiving_ac'),
        receiver_name: form.getFieldValue('receiver_name'),
        sending_ccy: senderAccountCurrency,
        narrative: form.getFieldValue('narrative'),
        doc_no: form.getFieldValue('doc_no'),
        sender_country: form.getFieldValue('sender_country'),
        sender_city: form.getFieldValue('sender_city'),
        sender_address: form.getFieldValue('sender_address'),
        receiver_country: form.getFieldValue('receiver_country'),
        receiver_city: form.getFieldValue('receiver_city'),
        receiver_bank_swift: form.getFieldValue('receiver_bank_swift'),
        receiver_bank_name: form.getFieldValue('receiver_bank_name'),
        receiver_bank_country: form.getFieldValue('receiver_bank_country'),
        receiver_address: form.getFieldValue('receiver_address'),
      })
    );

    dispatch(isSaveTemplateModalOpenAC(true));
  };

  const dummyRequest = ({ onSuccess }: any) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  return (
    <div className='md:grid grid-cols-6 gap-10 mt-5'>
      <div className='col-span-4'>
        <p className='text-xl mb-5 color-active font-semibold'>{t('International tjs')}</p>
        <div className=' mt-7'>
          <Templates type='SWIFT_TJS' />
        </div>
        <Divider className='my-6' />
        <SwiftTjsForm form={form} senderAccountCurrency={senderAccountCurrency} />
        <Form form={form} name='SwiftTjsDocument' onFinish={submit}>
          <p className='text-bs color-title mt-5 md:hidden'>{t('Document')}:</p>
          <div className='md:grid grid-cols-7 md:mt-7'>
            <p className='col-span-2 text-bs color-title max-md:hidden'>{t('Document')}:</p>
            <div className='col-span-5'>
              <Form.Item
                name='file'
                className='m-0'
                valuePropName='fileList'
                getValueFromEvent={e => e?.fileList}
                rules={[{ required: true, type: 'array', message: `${t('Required')}` }]}
              >
                <Upload
                  onChange={e => {
                    setFile(e.file);
                  }}
                  customRequest={dummyRequest}
                  listType='picture'
                  accept='.pdf'
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>{t('Upload')}</Button>
                </Upload>
              </Form.Item>
            </div>
          </div>

          <div className='flex justify-end mt-5'>
            <Button className='mr-3' type='default' onClick={openSaveTemplateModal}>
              {t('Save template')}
            </Button>
            <Button type='primary' htmlType='submit'>
              {t('Confirm')}
            </Button>
          </div>
        </Form>
        <SenderAccountsModal accounts={senderAccounts} selectHandler={senderAccountSelect} />
        <SearchSwiftModal selectSwiftHandler={selectSwiftHandler} />
        <SaveTemplateModal template={newSwiftTjsTemplate} />
        <ConfirmPaymentModal
          title={`${t('International tjs')}`}
          paymentInfo={swiftTjsQuery}
          currency={senderAccountCurrency}
          paymentTransactionHandler={swiftTjsTransaction}
          clearFields={clearFields}
          route={'swiftTjsPaymentStatus'}
          type='SWIFT_TJS'
        />
      </div>
      <div className='col-span-2 max-md:hidden'>
        <Currencies size='max-lg:text-xs text-sm' />

        <div className='bg-[#fffbea] p-6 rounded-lg' style={{ border: '1px solid #ffd230' }}>
          <div className='leading-7'>
            <p className='text-base font-semibold'>{t('swiftAttention.title')}</p>
            <p className='text-sm mt-3'>{t('swiftAttention.dearClients')}</p>
            <p className='text-sm'>
              {t('swiftAttention.payAttention')}
              <span className='font-semibold'>{t('swiftAttention.narrative')}</span>. <br /> {t('swiftAttention.file')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SwiftTjs;
